import React from 'react'
import Select, {components} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import TriangleIcon from '../../assets/svgs/triangle.svg'
import CloseIcon from '../../assets/svgs/close-thick.svg'
import PropTypes from 'prop-types'
import TooltipInfo from '../TooltipInfo'
import FieldExtra from '../FieldExtra'
import {SELECT_TYPES} from "../../consts";
import './select.scss'

const {DEFAULT, UNDERLINE, INLINE, TWO_LINES} = SELECT_TYPES;

const DropdownIndicator = () => {
  return <div className="Suc-dropdown-indicator"><TriangleIcon/>
  </div>
}

const ClearIndicator = props => {
  const {
    children = <div className="Suc-clear-indicator"><CloseIcon/></div>,
    innerProps: {ref, ...restInnerProps},
  } = props;
  return (
    <div
      {...restInnerProps}
      ref={ref}
    >
      {children}
    </div>
  )
}

const SingleValue = props => <components.SingleValue {...props}>
  {props.data.label}
</components.SingleValue>

const OptionWithSecondaryLabel = ({ label, label2 }) => <div className="Suc-option-with-secondary">
    <div className="Suc-option-label">{label}</div>
    <div className="Suc-option-secondary-label">{label2}</div>
  </div>

const Option = props => <div className="Suc-custom-option" title={props.data.disabled ? props.data.disabledTitle : props.data.label}>
    <components.Option {...props} className={props.data.__isNew__ ? "Suc-create-option" : ""}/>
  </div>

const defaultStyles = {
  menu: base => ({
    ...base,
    marginTop: 0
  })
};

const SelectField = (props) => {
  const {
    error, warning, label, options, isLoading, isSearchable, className, info, styles,
    placeholder, value, variant, disabled, removeBottomMargin, infoProps, enableOptionsAdding, components, ...rest
  } = props

  const extraClass = `Suc-${variant === UNDERLINE ? 'underline' : variant === INLINE ? 'inline' : 'default'}`
  const removeLabel = variant === UNDERLINE || variant === INLINE;

  const selectProps = {
    components: {
      DropdownIndicator,
      Option,
      SingleValue,
      ClearIndicator,
      ...components
    },
    className: `Suc-Select ${extraClass} ${error && 'Suc-error'}`,
    classNamePrefix: "Suc-Select",
    formatOptionLabel: variant === TWO_LINES ? OptionWithSecondaryLabel : undefined,
    defaultMenuIsOpen: false,
    onBlurResetsInput: false,
    onSelectResetsInput: false,
    captureMenuScroll: false,
    maxMenuHeight: 150,
    isSearchable,
    isClearable: false,
    placeholder,
    options,
    value,
    isLoading,
    styles: {...defaultStyles, ...styles},
    isDisabled: disabled,
    isOptionDisabled: option => option.disabled,
    ...rest
  }

  return (
    <div className={`Suc-Select-container ${className} ${disabled ? 'Suc-disabled' : ''} ${removeBottomMargin ? '' : 'Suc-bottom-margin'}`}>
      {label && !removeLabel && <div className={'Suc-select-top'}>
        {label && <label>{label}</label>}
        {info && <TooltipInfo {...infoProps}>{info}</TooltipInfo>}
      </div>}
      {!enableOptionsAdding && <Select {...selectProps}/>}
      {enableOptionsAdding && <CreatableSelect {...selectProps}/>}
      <FieldExtra error={error} warning={warning}/>
    </div>
  )
}
const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  label2: PropTypes.string, //Used in "two-lines" variant
  disabled: PropTypes.bool,
  disabledTitle: PropTypes.string
})

SelectField.propTypes = {
  /** select's type */
  variant: PropTypes.oneOf([DEFAULT, UNDERLINE, INLINE, TWO_LINES]),
  /** disabled state */
  disabled: PropTypes.bool,
  /** custom classname */
  className: PropTypes.string,
  /** label */
  label: PropTypes.string,
  /** placeholder */
  placeholder: PropTypes.string,
  /** error text */
  error: PropTypes.string,
  /** warning text */
  warning: PropTypes.string,
  /** remove margin-bottom (to allow inline alignment) */
  removeBottomMargin: PropTypes.bool,
  /** options array */
  options: PropTypes.arrayOf(optionShape),
  /** selected option */
  value: PropTypes.oneOfType([optionShape,PropTypes.arrayOf(optionShape)]),
  /** show loader */
  isLoading: PropTypes.bool,
  /** allow search */
  isSearchable: PropTypes.bool,
  /** info tooltip */
  info: PropTypes.node,
  /** InfoTooltip props */
  infoProps: PropTypes.object,
  /** can add values that are not in the options */
  enableOptionsAdding: PropTypes.bool,
}

SelectField.defaultProps = {
  className: '',
  variant: DEFAULT,
  error: '',
  warning: '',
  isSearchable: true,
  disabled: false,
  placeholder: ' - Select -',
  removeBottomMargin: false,
  infoProps: {},
  enableOptionsAdding: false,
  styles: {}
}

export default SelectField
